import React from "react";
import { FormControl, InputLabel, Select, MenuItem, Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { selectableSkills, skillOptionLabel } from "../../utils/skillUtils";

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FieldHelper = styled(Typography)(({ theme }) => ({
    display: "block",
    marginTop: theme.spacing(0.5),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
    },
}));

const HalfWidthField = styled(FormField)(({ theme }) => ({
    width: "50%",
    [theme.breakpoints.down("sm")]: {
        width: "100%",
    },
}));

const StepClassification = ({ form, onChange, templateType, groupTemplates, projectTemplates, departments = [], skills = [] }) => {
    return (
        <>
            <Row>
                <HalfWidthField>
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select name="priority" value={form.priority} onChange={onChange} label="Priority">
                            <MenuItem value="Lowest">Lowest</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Highest">Highest</MenuItem>
                        </Select>
                    </FormControl>
                </HalfWidthField>

                <HalfWidthField>
                    <FormControl fullWidth>
                        <InputLabel>Privacy</InputLabel>
                        <Select name="privacy" value={form.privacy} onChange={onChange} label="Privacy">
                            <MenuItem value="Public">Public</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                        </Select>
                    </FormControl>
                </HalfWidthField>
            </Row>

            <Row>
                <HalfWidthField>
                    <FormControl fullWidth>
                        <InputLabel>Department</InputLabel>
                        <Select name="department" value={form.department} onChange={onChange} label="Department">
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {departments.map((department) => (
                                <MenuItem key={department._id} value={department._id}>
                                    {department.name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FieldHelper variant="caption" color="text.secondary">
                            Carried onto everything this template creates. Leave as None to set it per run.
                        </FieldHelper>
                    </FormControl>
                </HalfWidthField>

                {templateType !== "group" && (
                    <HalfWidthField>
                        <FormControl fullWidth>
                            <InputLabel>Skill</InputLabel>
                            <Select name="skill" value={form.skill} onChange={onChange} label="Skill">
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {selectableSkills(skills, form.skill).map((skill) => (
                                    <MenuItem key={skill._id} value={skill._id}>
                                        {skillOptionLabel(skill)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FieldHelper variant="caption" color="text.secondary">
                                Sets the billing rate used when this template runs.
                            </FieldHelper>
                        </FormControl>
                    </HalfWidthField>
                )}
            </Row>

            {templateType === "project" && (
                <Row>
                    <HalfWidthField>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select name="status" value={form.status} onChange={onChange} label="Status">
                                <MenuItem value="Awaiting Authorization">Awaiting Authorization</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="In Progress">In Progress</MenuItem>
                                <MenuItem value="Testing">Testing</MenuItem>
                                <MenuItem value="Ready for Review">Ready for Review</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                                <MenuItem value="On Hold">On Hold</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </HalfWidthField>
                    <HalfWidthField>
                        <FormControl fullWidth>
                            <InputLabel>Group template (optional)</InputLabel>
                            <Select name="group" value={form.group} onChange={onChange} label="Group template (optional)">
                                <MenuItem value="">None</MenuItem>
                                {groupTemplates.map((g) => (
                                    <MenuItem key={g._id} value={g._id}>
                                        {g.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </HalfWidthField>
                </Row>
            )}

            {templateType === "task" && (
                <Row>
                    <HalfWidthField>
                        <FormControl fullWidth required>
                            <InputLabel>Parent project template</InputLabel>
                            <Select name="project" value={form.project} onChange={onChange} label="Parent project template">
                                {projectTemplates.map((p) => (
                                    <MenuItem key={p._id} value={p._id}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </HalfWidthField>
                    <HalfWidthField>
                        <FormControl fullWidth>
                            <InputLabel>Readiness</InputLabel>
                            <Select name="readiness" value={form.readiness} onChange={onChange} label="Readiness">
                                <MenuItem value="Ready">Ready</MenuItem>
                                <MenuItem value="Needs More Info">Needs More Info</MenuItem>
                                <MenuItem value="Blocked">Blocked</MenuItem>
                                <MenuItem value="Pending Payment">Pending Payment</MenuItem>
                            </Select>
                        </FormControl>
                    </HalfWidthField>
                </Row>
            )}
        </>
    );
};

export default StepClassification;
